import React, { createContext, useContext, useMemo } from 'react'
import { ClientError, GraphQLClient, Variables } from 'graphql-request'
import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { inlineFragments } from '../lib/inlineFragments'
import { removeUnusedVariables } from '../lib/removeUnusedVariables'
import { RawResponse, ResponseError, isErrorResponse } from '../lib/RequestUtils'
import { useCore, useLogin } from '../hooks'
import { getEndpoint } from 'depoto-core'
import { ENDPOINT } from 'depoto-core'
import { useNetInfo } from '../contexts/NetInfoContext'

// Create context for GQL client
export const GQLContext = createContext<GraphQLClient | null>(null)

// Hook to use GQL client
export const useGQL = () => {
  const gql = useContext(GQLContext)
  if (!gql) {
    throw new Error('useGQL must be used within a GQLProvider')
  }
  return gql
}

// Add a deduplicated token refresh function
let refreshPromise: Promise<any> | null = null
function refreshToken(core: any): Promise<any> {
  if (!refreshPromise) {
    refreshPromise = core.services.oauth.getRefreshToken().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

// GQL Provider component
export const GQLProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoggedIn } = useLogin()
  const { core } = useCore()
  // track token timestamp to recreate client after refresh
  const tokenTimestamp = core.services.oauth.session.token?.timestamp
  const endpoint = getEndpoint(ENDPOINT.GRAPHQL_POST, core.services.oauth.getSession())

  const Client = useMemo(() => {
    if (!isLoggedIn) {
      console.log('Not authenticated, returning dummy client')
      return new GraphQLClient('http://localhost:3000/graphql', {
        method: 'POST',
        headers: {},
      })
    }

    return new GraphQLClient(endpoint, {
      method: 'POST',
      // @ts-ignore (trust me bro™)
      requestMiddleware: async request => {
        // Due to our BE not supporting Fragments, we should inline them.
        const body = request.body as string
        const parsedBody = JSON.parse(body)

        const inlineFragmentsQuery = inlineFragments(parsedBody.query)

        // First inline fragments, then remove unused variables
        const processedQuery = removeUnusedVariables(inlineFragmentsQuery, parsedBody.variables)

        // @ts-ignore (trust me bro™)
        request.headers = core.services.http.getAuthorizationHeaders()

        request.body = JSON.stringify({
          query: processedQuery,
          variables: parsedBody.variables,
        })
        return request
      },
      // Removed responseMiddleware to centralize refresh in fetcher
    })
  }, [isLoggedIn, endpoint, tokenTimestamp])

  return <GQLContext.Provider value={Client}>{children}</GQLContext.Provider>
}

// Custom fetcher hook for GraphQL queries
export const useGQLFetcher = <TData extends RawResponse<unknown>, TVariables extends Variables>(
  query: TypedDocumentNode<TData, TVariables>,
): ((variables?: TVariables) => Promise<TData>) => {
  const client = useGQL()
  const { isLoggedIn, handleAuthInit, handleLogout } = useLogin()
  const { core } = useCore()
  const { reportRequest } = useNetInfo()
  const endpoint = getEndpoint(ENDPOINT.GRAPHQL_POST, core.services.oauth.getSession())

  return async (variables?: TVariables) => {
    if (!isLoggedIn) {
      throw new Error('Not authenticated')
    }

    try {
      // @ts-ignore - graphql-request v7 typing issue
      const result = await client.request(query, variables)
      tryToParseAndThrowError(result)
      // Report successful request
      reportRequest(endpoint, 'success')
      return result
    } catch (error: any) {
      // If unauthorized, attempt token refresh and retry once
      if (error instanceof ClientError && error.response.status === 401) {
        const refreshed = await refreshToken(core)
        if (refreshed) {
          // Rehydrate login state after refresh
          await handleAuthInit(core)
          // Retry the original request
          try {
            // @ts-ignore - graphql-request v7 typing issue for retry call
            const result = await client.request(query, variables)
            // Report successful retry
            reportRequest(endpoint, 'success')
            return result
          } catch (retryError: any) {
            throw retryError
          }
        } else {
          // Refresh failed, clear login state
          handleLogout(core)
          throw new Error('Session expired')
        }
      }
      // Handle GraphQL error responses
      if (error?.response?.errors) {
        processAndThrowErrorField(error.response.errors)
      }
      const message = error instanceof Error ? error.message : 'Unknown GraphQL error'
      throw new Error(message)
    }
  }
}

function processAndThrowErrorField(errorField: ResponseError) {
  if (Array.isArray(errorField)) {
    if (errorField.length > 0 && typeof errorField[0] === 'object') {
      throw new Error(
        (errorField as unknown as { message?: string; error?: string }[])
          .map(el => el?.message || el?.error || 'Unknown GraphQL error')
          .join(', '),
      )
    } else if (errorField.length > 0 && typeof errorField[0] === 'string') {
      throw new Error(errorField.join(', '))
    }
  }

  if (typeof errorField === 'string') {
    throw new Error(errorField)
  }

  if (errorField && typeof errorField === 'object' && 'message' in errorField) {
    processAndThrowErrorField(errorField.message)
  }

  throw new Error('Unknown GraphQL error (failed to parse error field)')
}

function tryToParseAndThrowError<TData>(response: RawResponse<TData>): void {
  if (isErrorResponse(response) && response) {
    const keys = Object.keys(response)
    if (keys.length > 0) {
      const firstKey = keys[0]
      const entityData = response[firstKey]
      if (entityData && 'errors' in entityData && entityData.errors) {
        processAndThrowErrorField(entityData.errors)
      }
    }
  }
}
